/*
 * Copyright (c) 2023, WSO2 LLC (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 LLC and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import React from "react";
import { FormattedMessage} from "react-intl";

import Button from "@material-ui/core/Button";
import { Typography, Box } from "@material-ui/core";
import classNames from "classnames";
import EmailIcon from '../../icons/Email';

import { useStyles } from "./style";
import Settings from "../../conf/settings"

interface LoginCardProps {
    handleEmailLogin: () => void,
    handleGithubLogin: () => void,
    handleGoogleLogin: () => void,
    handleMicrosoftLogin: () => void,
}

function LoginCard(loginCardProps: LoginCardProps) {
    const classes = useStyles();
    const { handleEmailLogin, handleGithubLogin, handleGoogleLogin, handleMicrosoftLogin } = loginCardProps;

    const GoogleButtonText = () => {
        if (Settings.enableEmailLogin) {
            return (
                <FormattedMessage
                    id="components.login.index.continue.withGoogle"
                    defaultMessage="Continue with Google"
                />
            );
        }
        return (
            <FormattedMessage
                id="components.login.index.signIn.withGoogle"
                defaultMessage="Sign in with Google"
            />
        );
    };

    const GithubButtonText = () => {
        if (Settings.enableEmailLogin) {
            return (
                <FormattedMessage
                    id="components.login.index.continue.withGithub"
                    defaultMessage="Continue with GitHub"
                />
            );
        }
        return (
            <FormattedMessage
                id="components.login.index.signIn.withGithub"
                defaultMessage="Sign in with GitHub"
            />
        );
    };

    const MicrosoftButtonText = () => {
        if (Settings.enableEmailLogin) {
            return (
                <FormattedMessage
                    id="components.login.index.continue.withMicrosoft"
                    defaultMessage="Continue with Microsoft"
                />
            );
        }
        return (
            <FormattedMessage
                id="components.login.index.signIn.withMicrosoft"
                defaultMessage="Sign in with Microsoft"
            />
        );
    };

    return (
        <React.Fragment>
            {Settings.enableEmailLogin && (
                <Box>
                    <Button
                        className={classNames(
                            classes.loginBtn,
                            classes.email
                        )}
                        variant="contained"
                        onClick={handleEmailLogin}
                        aria-label="Email sign in"
                        data-cyid="sign-in-with-email"
                    >
                        <Box className={classes.emailBtnIcon}>
                            <EmailIcon className={classes.emailIcon} />
                        </Box>
                        <FormattedMessage
                            id="components.login.buttons.login.with.email"
                            defaultMessage="Sign in with Email"
                        />
                    </Button>
                    <Typography variant="subtitle2" className={classes.orDividerText}>
                        <FormattedMessage
                            id="components.login.index.orDivider"
                            defaultMessage="OR"
                        />
                    </Typography>
                </Box>
            )}
            <Button
                className={classNames(
                    classes.loginBtn,
                    classes.github
                )}
                variant="contained"
                onClick={handleGithubLogin}
                aria-label="GitHub sign in"
                data-cyid="sign-in-with-github"
            >
                <img
                    src="themes/default/images/github.svg"
                    className={classes.btnIcon}
                    alt="GitHub Logo"
                />
                <GithubButtonText />
            </Button>
            <Button
                className={classNames(
                    classes.loginBtn,
                    classes.google
                )}
                variant="contained"
                onClick={handleGoogleLogin}
                aria-label="Google sign in"
                data-cyid="sign-in-with-google"
            >
                <img
                    src="/themes/default/images/google.svg"
                    className={classes.btnIcon}
                    alt="Google Logo"
                />
                <GoogleButtonText />
            </Button>
            {Settings.enableMicrosoftLogin && (
                <Button
                    className={classNames(
                        classes.loginBtn,
                        classes.microsoft
                    )}
                    variant="contained"
                    onClick={handleMicrosoftLogin}
                    aria-label="Microsoft sign in"
                    data-cyid="sign-in-with-microsoft"
                >
                    <img
                        src="/themes/default/images/microsoft.svg"
                        className={classes.msftBtnIcon}
                        alt="Microsoft Logo"
                    />
                    <MicrosoftButtonText />
                </Button>
            )}
        </React.Fragment>
    );
}

export default LoginCard;
